// src/components/profile/Documents/PopupsCard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Edit2, Trash2, Eye, Play, Pause, RefreshCw, Plus, X, FileText, Loader2, Clock } from 'lucide-react';
import Swal from 'sweetalert2';
import { Banner } from '../../../components/common/Banner';
import TooltipHelper from '../../../components/common/TooltipHelper';
import SectionTips from '../../../components/common/SectionTips';
import { getAllPopups, createPopup, updatePopup, deletePopup, rotatePopups, resetLoop, pauseRotation, resumeRotation, getRotationStatus, updateRotationDuration } from '../../../services/popupsService';
import PopupModal from '../../../components/common/PopupModal';

const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] disabled:opacity-60 disabled:cursor-not-allowed`;
const btnGhost = `${btn} border bg-white hover:bg-gray-50 disabled:opacity-60`;
const input = "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";

const ChipTitle = ({ children }) => (
  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
    <span className="inline-block w-1.5 h-5 bg-[#0488AF] rounded-full" />
    {children}
  </div>
);

function GradientCard({ children }) {
  return (
    <div
      className="rounded-2xl p-[1.5px]"
      style={{ background: "linear-gradient(135deg,#E6F0FF 0%,#E6FBFF 100%)" }}
    >
      <div className="rounded-2xl bg-white ring-1 ring-black/5">{children}</div>
    </div>
  );
}

export default function PopupsCard({ onBack }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    if (window.history.length > 1) return navigate(-1);
    navigate("/profile");
  };

  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [rotationStatus, setRotationStatus] = useState({
    isPaused: false,
    durationMinutes: 2,
    cronSchedule: 'Cada 2 minutos',
  });
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [newDuration, setNewDuration] = useState(2);
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    file: null,
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadPopups();
    loadRotationStatus();
  }, []);

  const loadRotationStatus = async () => {
    try {
      const status = await getRotationStatus();
      // Asegurar que el estado se actualice correctamente
      setRotationStatus(prev => ({
        ...prev,
        ...status,
      }));
    } catch (error) {
      // Mantener estado por defecto si falla
      setRotationStatus({
        isPaused: false,
        durationMinutes: 2,
        cronSchedule: 'Cada 2 minutos',
      });
    }
  };

  const loadPopups = async () => {
    try {
      setLoading(true);
      const data = await getAllPopups();
      const sorted = data.sort((a, b) => (a.position || 0) - (b.position || 0));
      setPopups(sorted);
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al cargar pop-ups', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        Swal.fire('Error', 'Por favor, selecciona un archivo de imagen (PNG, JPG, JPEG, etc.)', 'error');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('Error', 'El archivo es demasiado grande. Máximo 10MB', 'error');
        return;
      }
      setFormData({ ...formData, file });
    }
  };

  const handleUpload = async () => {
    if (!formData.file) {
      Swal.fire('Error', 'Por favor, selecciona una imagen', 'error');
      return;
    }

    try {
      setUploading(true);
      await createPopup(formData.file, {
        title: formData.title || undefined,
        link: formData.link || undefined,
      });

      await Swal.fire({
        icon: 'success',
        title: 'Pop-up creado',
        text: 'La imagen se subió correctamente',
        timer: 2000,
        showConfirmButton: false,
      });

      setShowUploadModal(false);
      setFormData({ title: '', link: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadPopups();
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al crear pop-up', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (popup) => {
    const result = await Swal.fire({
      title: '¿Eliminar pop-up?',
      text: `¿Estás seguro de eliminar "${popup.title || 'este pop-up'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deletePopup(popup.id);
        await Swal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: 'Pop-up eliminado correctamente',
          timer: 1500,
          showConfirmButton: false,
        });
        await loadPopups();
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al eliminar pop-up', 'error');
      }
    }
  };

  const handleToggleActive = async (popup) => {
    try {
      await updatePopup(popup.id, { isActive: !popup.isActive });
      await loadPopups();
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al actualizar pop-up', 'error');
    }
  };

  const handleRotate = async () => {
    if (rotationStatus.isPaused) {
      Swal.fire('Advertencia', 'No se puede rotar mientras el ciclo está pausado', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: '¿Ejecutar rotación?',
      text: 'Esto activará el siguiente pop-up en la cola',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, rotar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await rotatePopups();
        await Swal.fire({
          icon: 'success',
          title: 'Rotación ejecutada',
          timer: 1500,
          showConfirmButton: false,
        });
        // Recargar datos para reflejar cambios
        await Promise.all([loadPopups(), loadRotationStatus()]);
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al rotar pop-ups', 'error');
      }
    }
  };

  const handleResetLoop = async () => {
    if (rotationStatus.isPaused) {
      Swal.fire('Advertencia', 'No se puede reiniciar el loop mientras el ciclo está pausado', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: '¿Reiniciar loop?',
      text: 'Esto reiniciará el ciclo desde el primer pop-up y reprogramará todos',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, reiniciar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await resetLoop();
        await Swal.fire({
          icon: 'success',
          title: 'Loop reiniciado',
          text: 'El ciclo de pop-ups se reinició desde el principio',
          timer: 2000,
          showConfirmButton: false,
        });
        // Recargar datos para reflejar cambios
        await Promise.all([loadPopups(), loadRotationStatus()]);
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al reiniciar loop', 'error');
      }
    }
  };

  const handlePauseRotation = async () => {
    const result = await Swal.fire({
      title: '¿Pausar el ciclo de rotación?',
      text: 'Esto detendrá la rotación automática y desactivará todos los pop-ups activos',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, pausar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await pauseRotation();
        await Swal.fire({
          icon: 'success',
          title: 'Ciclo pausado',
          text: 'La rotación automática se ha pausado',
          timer: 2000,
          showConfirmButton: false,
        });
        // Recargar datos para reflejar cambios
        await Promise.all([loadPopups(), loadRotationStatus()]);
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al pausar rotación', 'error');
      }
    }
  };

  const handleResumeRotation = async () => {
    const result = await Swal.fire({
      title: '¿Reanudar el ciclo de rotación?',
      text: 'Esto reactivará la rotación automática desde el primer pop-up',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, reanudar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await resumeRotation();
        await Swal.fire({
          icon: 'success',
          title: 'Ciclo reanudado',
          text: 'La rotación automática se ha reanudado',
          timer: 2000,
          showConfirmButton: false,
        });
        // Recargar datos para reflejar cambios
        await Promise.all([loadPopups(), loadRotationStatus()]);
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al reanudar rotación', 'error');
      }
    }
  };

  const handleEditDuration = async () => {
    setNewDuration(rotationStatus.durationMinutes);
    setShowDurationModal(true);
  };

  const handleSaveDuration = async () => {
    if (newDuration < 1 || newDuration > 10080) {
      Swal.fire('Error', 'La duración debe estar entre 1 y 10080 minutos (7 días)', 'error');
      return;
    }

    const result = await Swal.fire({
      title: '¿Actualizar duración?',
      text: `La duración de rotación cambiará a ${newDuration} minuto(s). Esto afectará a todos los pop-ups futuros.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await updateRotationDuration(newDuration);
        await Swal.fire({
          icon: 'success',
          title: 'Duración actualizada',
          text: `La duración de rotación se actualizó a ${newDuration} minuto(s)`,
          timer: 2000,
          showConfirmButton: false,
        });
        await loadRotationStatus();
        setShowDurationModal(false);
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al actualizar duración', 'error');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="w-full overflow-x-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="max-w-screen-xl mx-auto px-4 py-6 space-y-6"
      >
        <button
          onClick={goBack}
          className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
        >
          <ArrowLeft size={18} /> Volver al menú principal
        </button>

        <Banner
          title="Pop-ups"
          message="Sube, edita, organiza y controla los pop-ups que se muestran a los administradores."
        />

        <SectionTips
          section="la sección de Pop-ups"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo usar la gestión de Pop-ups?"
          tipsList={[
            'Los pop-ups son mensajes que se muestran exclusivamente a administradores al iniciar sesión, una vez por sesión.',
            'Puedes subir imágenes en formato PNG, JPG o JPEG de máximo 10 MB para crear nuevos pop-ups.',
            'Cada pop-up tiene una posición en la rotación que determina el orden en que se mostrarán.',
            'Puedes configurar la duración de visualización de cada pop-up antes de que se muestre el siguiente.',
            'El botón "Pausar ciclo" detiene la rotación automática y desactiva todos los pop-ups activos.',
            'El botón "Reanudar ciclo" reactiva la rotación desde el primer pop-up programado.',
            'Puedes editar la duración de rotación haciendo clic en el icono de edición junto a "Duración" en el panel de estado.',
            'El botón de cerrar (X) en los pop-ups aparece después de 3.5 segundos para asegurar que el mensaje sea visto.',
            'Puedes activar o desactivar pop-ups individualmente usando el botón de play/pausa en cada tarjeta.',
            'La rotación automática cambia de pop-up según la duración configurada y el cron programado.',
          ]}
          finalNote="Consejo: Organiza tus pop-ups por importancia, ya que solo se mostrarán una vez por inicio de sesión de cada administrador."
          color="#0488AF"
          videoUrl="" // TODO: Agregar URL del video tutorial
          manualUrl="" // TODO: Agregar URL del manual de usuario
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:items-start min-w-0">
          {/* Subir */}
          <div
            className="rounded-2xl p-[1.5px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5">
                <ChipTitle>Subir nuevo pop-up</ChipTitle>
                <div className="mt-3 flex items-center">
                  <TooltipHelper text="Selecciona una imagen (PNG, JPG, JPEG) de máximo 10 MB. El pop-up se mostrará en rotación a los administradores." />
                </div>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Título (opcional)
                    </label>
                    <input
                      type="text"
                      className={input}
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Ej: Promoción especial"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Imagen <span className="text-red-500">*</span>
                    </label>
                    <label className={`${btnGhost} cursor-pointer`}>
                      <Upload size={16} />
                      Seleccionar imagen
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                    {formData.file && (
                      <div className="mt-3">
                        <div className="flex items-center gap-3 p-3 rounded-xl border bg-slate-50">
                          <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                            <FileText className="w-5 h-5 text-[#0488AF]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">
                              {formData.file.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(formData.file.size / (1024 * 1024)).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`${btnGhost} shrink-0`}
                            onClick={() => {
                              setFormData({ ...formData, file: null });
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            disabled={uploading}
                            title="Quitar archivo"
                          >
                            <X size={16} /> Quitar
                          </button>
                        </div>
                        <div className="mt-2">
                          <img
                            src={URL.createObjectURL(formData.file)}
                            alt="Preview"
                            className="w-full h-32 object-contain rounded-lg border"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                      onClick={handleUpload}
                      disabled={!formData.file || uploading}
                      className={btnPri}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />
                          Subiendo…
                        </>
                      ) : (
                        <>
                          <Upload size={16} />
                          Subir pop-up
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div
            className="rounded-2xl p-[2px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #DCEBFF 0%, #DDF8FF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5 space-y-6">
                <ChipTitle>Acciones de gestión</ChipTitle>
                
                {/* Estado de rotación */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">Estado del ciclo</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      rotationStatus.isPaused 
                        ? 'bg-red-100 text-red-700 border border-red-200' 
                        : 'bg-green-100 text-green-700 border border-green-200'
                    }`}>
                      {rotationStatus.isPaused ? 'Pausado' : 'Activo'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1 mb-3">
                    <div className="flex items-center justify-between">
                      <span>Duración: <span className="font-semibold">{rotationStatus.durationMinutes} minuto(s)</span> por pop-up</span>
                      <button
                        onClick={handleEditDuration}
                        className="p-1.5 rounded-lg hover:bg-[#0488AF]/10 text-[#0488AF] transition-colors"
                        title="Editar duración"
                      >
                        <Edit2 size={14} />
                      </button>
                    </div>
                    <div>Rotación: <span className="font-semibold">{rotationStatus.cronSchedule}</span></div>
                  </div>
                </div>

                <div className="space-y-3">
                  {rotationStatus.isPaused ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-700">Reanudar ciclo</span>
                        <TooltipHelper text="Reanuda la rotación automática de pop-ups." />
                      </div>
                      <button
                        type="button"
                        onClick={handleResumeRotation}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-white transition-all w-full justify-center"
                        style={{
                          background: 'linear-gradient(to right, #09274A, #0488AF)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(to right, #071f3d, #03779C)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(to right, #09274A, #0488AF)';
                        }}
                      >
                        <Play size={18} />
                        Reanudar Ciclo
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-700">Pausar ciclo</span>
                        <TooltipHelper text="Pausa la rotación automática y desactiva todos los pop-ups activos." />
                      </div>
                      <button
                        type="button"
                        onClick={handlePauseRotation}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-white transition-all w-full justify-center"
                        style={{
                          background: 'linear-gradient(to right, #DC3465, #C42251)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(to right, #C42251, #A01D45)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(to right, #DC3465, #C42251)';
                        }}
                      >
                        <Pause size={18} />
                        Pausar Ciclo
                      </button>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-700">Rotar pop-ups</span>
                      <TooltipHelper text="Activa el siguiente pop-up en la cola de rotación." />
                    </div>
                    <button
                      type="button"
                      onClick={handleRotate}
                      className={`${btnPri} w-full justify-center`}
                      disabled={rotationStatus.isPaused}
                    >
                      <Play size={18} />
                      Rotar Pop-ups
                    </button>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-700">Reiniciar loop</span>
                      <TooltipHelper text="Reinicia el ciclo de rotación desde el primer pop-up y reprograma todos." />
                    </div>
                    <button
                      type="button"
                      onClick={handleResetLoop}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-white transition-all w-full justify-center"
                      style={{
                        background: 'linear-gradient(to right, #0488AF, #09274A)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #03779C, #071f3d)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #0488AF, #09274A)';
                      }}
                      disabled={rotationStatus.isPaused}
                    >
                      <RefreshCw size={18} />
                      Reiniciar Loop
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de pop-ups */}
        <div className="mt-6">
          <ChipTitle className="mb-4">Pop-ups existentes</ChipTitle>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Cargando...</div>
          ) : popups.length === 0 ? (
            <GradientCard>
              <div className="p-5 text-center py-12 text-gray-500">
                <p className="mb-4">No hay pop-ups subidos</p>
                <button
                  onClick={() => {
                    document.querySelector('input[type="file"]')?.click();
                  }}
                  className={btnPri}
                >
                  <Plus size={18} />
                  Subir tu primer pop-up
                </button>
              </div>
            </GradientCard>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popups.map((popup) => (
                <GradientCard key={popup.id} className="h-full">
                  <div className="p-5 h-full flex flex-col">
                    <div className="border rounded-xl p-4 flex flex-col gap-4 hover:shadow-sm transition-shadow bg-white/70 min-w-0 flex-1">
                      {popup.imageUrl && (
                        <div className="w-full aspect-video rounded-lg overflow-hidden border bg-gray-50">
                          <img
                            src={popup.imageUrl}
                            alt={popup.title || 'Pop-up'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 flex flex-col min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg text-gray-900 truncate">
                            {popup.title || 'Sin título'}
                          </h3>
                          {popup.isActive && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 shrink-0">
                              Activo
                            </span>
                          )}
                        </div>
                        {popup.link && (
                          <a
                            href={popup.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#0488AF] hover:underline truncate block mb-2"
                          >
                            {popup.link}
                          </a>
                        )}
                        <div className="text-xs text-gray-600 space-y-0.5 mt-auto">
                          <div>Creado: {formatDate(popup.createdAt)}</div>
                          <div>Posición: {popup.position || 0}</div>
                          {popup.startDate && (
                            <div>Inicio: {formatDate(popup.startDate)}</div>
                          )}
                          {popup.endDate && (
                            <div>Fin: {formatDate(popup.endDate)}</div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 shrink-0 w-full justify-end pt-2 border-t">
                        <button
                          onClick={() => {
                            setSelectedPopup(popup);
                            setShowPreview(true);
                          }}
                          className="px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                          title="Vista previa"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(popup)}
                          className={`px-3 py-1.5 rounded-lg border ${
                            popup.isActive
                              ? 'bg-green-50 text-green-700 hover:bg-green-100'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                          title={popup.isActive ? 'Desactivar' : 'Activar'}
                        >
                          {popup.isActive ? <Pause size={16} /> : <Play size={16} />}
                        </button>
                        <button
                          onClick={() => handleDelete(popup)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 size={16} /> Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </GradientCard>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Modal de vista previa */}
      {showPreview && selectedPopup && (
        <PopupModal
          popup={selectedPopup}
          onClose={() => {
            setShowPreview(false);
            setSelectedPopup(null);
          }}
        />
      )}

      {/* Modal para editar duración */}
      {showDurationModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="text-[#0488AF]" size={20} />
                Editar duración de rotación
              </h3>
              <button
                onClick={() => setShowDurationModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duración en minutos (1-10080, máximo 7 días)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10080"
                  value={newDuration}
                  onChange={(e) => setNewDuration(Number(e.target.value))}
                  className={input}
                  placeholder="Ej: 4320 (3 días), 1440 (1 día)..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cada pop-up se mostrará durante esta cantidad de minutos antes de rotar al siguiente.
                  <br />
                  <span className="text-gray-600 font-medium">Ejemplos:</span> 1440 min = 1 día, 4320 min = 3 días, 10080 min = 7 días
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowDurationModal(false)}
                  className={`${btnGhost} flex-1 justify-center`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveDuration}
                  className={`${btnPri} flex-1 justify-center`}
                >
                  <Clock size={16} />
                  Guardar duración
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

